/**
 * Utilities for handling Convex errors on the frontend.
 *
 * Convex uses `ConvexError` for application-level errors that should be
 * handled gracefully (not treated as unexpected exceptions).
 *
 * @see packages/convex/convex/lib/auth.ts for error definitions
 */

// Import and re-export auth error types from shared package (single source of truth)
import {
  type AppErrorData,
  AUTH_ERROR_CODES,
  type AuthErrorCode,
  type AuthErrorData,
  isAppErrorData,
  isAuthErrorData,
  isExpectedAppErrorCode,
} from "@repo/shared";
import { ConvexError } from "convex/values";

export { AUTH_ERROR_CODES, type AuthErrorCode, type AuthErrorData };

// ============================================================================
// Error Type Guards
// ============================================================================

/**
 * Check if an error is a ConvexError.
 * Note: We use a simple instanceof check rather than type predicate
 * because ConvexError's generic constraint requires a Convex Value type.
 */
export function isConvexError(error: unknown): boolean {
  return error instanceof ConvexError;
}

/**
 * Check if an error is an authentication error from the backend.
 * These errors are expected and should be handled gracefully.
 *
 * @example
 * ```typescript
 * try {
 *   await someConvexMutation();
 * } catch (error) {
 *   if (isAuthError(error)) {
 *     // Handle auth error (e.g., redirect to login)
 *     return;
 *   }
 *   // Re-throw unexpected errors
 *   throw error;
 * }
 * ```
 */
export function isAuthError(error: unknown): boolean {
  if (!isConvexError(error)) return false;

  // Access data from ConvexError instance and validate shape
  const convexError = error as ConvexError<AuthErrorData>;
  return isAuthErrorData(convexError.data);
}

/**
 * Get the auth error code from a ConvexError.
 * Returns undefined if not an auth error.
 */
export function getAuthErrorCode(error: unknown): AuthErrorCode | undefined {
  if (!isAuthError(error)) return undefined;
  const convexError = error as ConvexError<AuthErrorData>;
  return convexError.data.code;
}

/**
 * Extract coded application error data (`APP_ERROR_CODES`) from a ConvexError.
 * Returns undefined for auth errors, plain errors, and redacted server errors.
 */
export function getAppErrorData(error: unknown): AppErrorData | undefined {
  if (!isConvexError(error)) return undefined;
  const data = (error as ConvexError<AppErrorData>).data;
  return isAppErrorData(data) ? data : undefined;
}

/**
 * Check if an error is an expected application failure that the UI handles as a
 * normal outcome. Expected failures get warning breadcrumbs, never Sentry capture —
 * LAT-839 burned a triage cycle on an E2E negative-path test reported this way.
 *
 * The code list itself is `EXPECTED_APP_ERROR_CODES` in `@repo/shared`, shared with
 * the backend's `classifyTrackedError` so both Sentry pipelines agree on what
 * "expected" means (LAT-910).
 */
export function isExpectedAppError(error: unknown): boolean {
  const data = getAppErrorData(error);
  return data !== undefined && isExpectedAppErrorCode(data.code);
}

export function getConvexErrorMessage(error: unknown): string | undefined {
  if (!isConvexError(error)) return undefined;

  const data = (error as ConvexError<string | { message?: string }>).data;
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  if (data && typeof data === "object" && "message" in data) {
    const message = (data as { message?: unknown }).message;
    return typeof message === "string" && message.trim() ? message.trim() : undefined;
  }
  return undefined;
}

/**
 * Check if an error indicates the user is not authenticated.
 * This is useful for triggering login redirects.
 */
export function isNotAuthenticatedError(error: unknown): boolean {
  const code = getAuthErrorCode(error);
  return code === AUTH_ERROR_CODES.NOT_AUTHENTICATED || code === AUTH_ERROR_CODES.INVALID_AUTH;
}

/**
 * Check if an auth error represents expected missing identity state.
 *
 * This is intentionally narrower than `isNotAuthenticatedError`: INVALID_AUTH
 * can indicate JWT/template/configuration problems and should stay visible.
 */
export function isExpectedUnauthenticatedError(error: unknown): boolean {
  return getAuthErrorCode(error) === AUTH_ERROR_CODES.NOT_AUTHENTICATED;
}

/**
 * Check if an error indicates the user lacks organization access.
 */
export function isOrgAccessError(error: unknown): boolean {
  const code = getAuthErrorCode(error);
  return code === AUTH_ERROR_CODES.NOT_ORG_MEMBER || code === AUTH_ERROR_CODES.USER_NOT_FOUND;
}
